import React, { useCallback, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { useAppDisaptch, useAppSelector } from '../redux/hooks';
import { useHttpClient } from './useHttpClient';
import {
  getFilters,
  resetFilter,
  updateOptions,
  updateValue,
} from '../redux/slices/filters';
import { clearOils, getOils, storeOils } from '../redux/slices/oils';
import type { NextFilterResponse, OilsResponse } from '../types/response.type';
import type { QueryOptions } from '../types/queryOptions.type';
import type { FilterOptions } from '../types/filter.types';
import type { Filter } from '../types/OilsFIlter.types';

export const useOilsFilters = (
  countries: FilterOptions['options'],
  { dbName, getNextFilter, initialFilterItem, lang, sitename }: QueryOptions
) => {
  const dispatch = useAppDisaptch();
  const httpClient = useHttpClient(getNextFilter);

  const filters = useAppSelector(getFilters);
  const oils = useAppSelector(getOils);

  // ----- set initial countries options -----
  useEffect(() => {
    dispatch(updateOptions({ filter: 'country', options: countries }));
  }, [countries, dispatch]);

  // ----- clear oils results if not all filters are set -----
  useEffect(() => {
    if (
      Object.keys(filters).filter((filter: Filter) => !filters[filter].value)
        .length > 0 &&
      oils.length > 0
    ) {
      dispatch(clearOils());
    }
  }, [filters]);

  // ----- filter change handler -----
  const handleFilterChange = useCallback(
    async (filterValue: string, filterName: Filter) => {
      // --- get selected option id value ---
      const filterIdValue = filters[filterName].options.find((o) => o.name === filterValue)?.id ??
      '';
       

      // --- update store with selected value and save pervious value ---
      const prevValue = filters[filterName].value;
      dispatch(updateValue({ filter: filterName, value: filterIdValue }));

      // --- if filter value was reset, clear dependants ---
      if (filterIdValue.length === 0) resetDependats(filterName);
      else {
        // --- if filter value had previous value, clear dependants ---
        if (prevValue.length > 0) resetDependats(filterName);
        // --- build query params for consequent filter request ---
        const params =
          filterName === 'country'
            ? buildParams(initialFilterItem, filters[filterName].options.find((o) => o.name === filterValue)?.parentId ??
            '')
            : buildParams(filterIdValue, filters.country.value);

        // --- get next filter name ---
        const nextFilter = NEXT_FILTER_MAP.get(filterName);

        // --- fetch data ---
        const data = await handleDataFetch(params, !nextFilter);

        // --- store result in next filter options or oils list ---
        if (nextFilter) {
          dispatch(
            updateOptions({
              filter: nextFilter,
              options: data as FilterOptions['options'],
            })
          );
        } else {
          dispatch(storeOils(data as OilsResponse));
        }
      }
    },
    [filters]
  );

  // ----- delete data of filter dependants -----
  const resetDependats = useCallback(
    (filterName: Filter) =>
      FILTER_DEPANDANTS.get(filterName).forEach((dependant) => {
        dispatch(resetFilter(dependant));
      }),
    [dispatch]
  );

  // ----- build query params from filter item & country -----
  const buildParams = useCallback((filterItem: string, country: string) => {
    return `siteName=${sitename}&language=${lang}&database=${dbName}&filterItem={${filterItem.toUpperCase()}}&country={${country.toUpperCase()}}`;
  }, []);

  // ----- data fetch handler -----
  const handleDataFetch: (
    params: string,
    isOil?: boolean
  ) => Promise<FilterOptions['options'] | OilsResponse> = useCallback(
    async (params, isOil = false) => {
      const response = await httpClient.get<
        NextFilterResponse[] | OilsResponse
      >('', {
        params,
        paramsSerializer: (p: string) => p,
      });

      if (isOil) {
        return response.data;
      }

      // --- if oils are not expected cast response to NextFilter type ad perform format ---
      const formattedOptions: FilterOptions['options'] = (
        response as AxiosResponse<NextFilterResponse[], any>
      ).data.map((item) => ({
        id: item.id,
        name: item?.fields?.Title?.value ?? item.name,
      }));
      return formattedOptions;
    },
    []
  );

  return handleFilterChange;
};

const NEXT_FILTER_MAP = new Map<Filter, Filter>([
  ['country', 'application'],
  ['application', 'subapplication'],
  ['subapplication', 'series'],
  ['series', 'homoAndMiss'],
]);

const FILTER_DEPANDANTS = new Map<Filter, Filter[]>([
  ['country', ['application', 'subapplication', 'series', 'homoAndMiss']],
  ['application', ['subapplication', 'series', 'homoAndMiss']],
  ['subapplication', ['series', 'homoAndMiss']],
  ['series', ['homoAndMiss']],
  ['homoAndMiss', []],
]);
